import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";

interface ConfirmDeleteDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  itemName?: string;
  title?: string;
  description?: string;
}

const ConfirmDeleteDialog: React.FC<ConfirmDeleteDialogProps> = ({
  open,
  onClose,
  onConfirm,
  itemName = "",
  title = "Confirm Deletion",
  description = "This action is permanent and cannot be undone. All associated data will be lost."
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        className: "shadow-xl", // Tailwind shadow class
        sx: {
          borderRadius: 2, // MUI style — 4 = 32px ≈ Tailwind's `rounded-2xl`
          overflow: "hidden", // prevents child overflow ruining rounding
        },
      }}
    >
      <div className="flex flex-col items-center text-center p-2 bg-white">
        {/* Icon */}
        <div className="bg-red-100 rounded-full p-2 shadow-sm animate-pulse mt-3">
          <WarningAmberIcon className="text-red-600" sx={{ fontSize: 30 }} />
        </div>

        {/* Title */}
        <DialogTitle className="!mt-1 text-xl font-bold text-red-700">
          Confirm Deletion
        </DialogTitle>

        {/* Message */}
        <DialogContent className="text-gray-600 px-1">
          <p className="text-base font-medium leading-relaxed">
            Are you sure you want to delete{" "}
            <span className="text-black font-semibold">
              {itemName}
            </span>
            ?
          </p>
          <p className="text-sm text-gray-400 mt-2">
            This action is permanent and cannot be undone. All associated data
            will be lost.
          </p>
        </DialogContent>

        {/* Actions */}
        <DialogActions className="w-full px-6 pt-4 pb-2 justify-center gap-1">
          <Button
            onClick={onClose}
            startIcon={<CloseIcon />}
            color="error"
            variant="outlined"
            className="rounded-full px-5 py-2 font-semibold normal-case"
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            startIcon={<DeleteIcon />}
            variant="contained"
            color="error"
            className="rounded-full px-5 py-2 font-semibold normal-case text-white"
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            Delete
          </Button>
        </DialogActions>
      </div>
    </Dialog>
  );
};

export default ConfirmDeleteDialog;
